import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Button, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotate, faLink, faCirclePlus } from "@fortawesome/free-solid-svg-icons";

import { DarkModeContext } from "../../context/DarkModeContext";
import logo from "../../assets/img/logo.png";

import BienvenidaMonitoreoOperativo from "../../components/PanelFlorenciaNuevo/BienvenidaMonitoreoOperativo";
import TablaCuitVinculadosOperativo from "../../components/PanelFlorenciaNuevo/TablaCuitVinculadosOperativo";
import TituloPagina from "../../components/comunes/TituloPagina";
import Footer from "../../components/comunes/Footer";
import "../../components/PanelFlorenciaNuevo/TablaCuitVinculadosOperativo.css";
import { getToken } from "../../utils/auth";


const CuitVinculadosOperativo = () => {
    const apiUrlToken = process.env.REACT_APP_API_TOKEN;
    const { darkMode } = useContext(DarkModeContext);
    const navegacion = useNavigate();

    const [datos, setDatos] = useState([]);
    const [loadingTabla, setLoadingTabla] = useState(true);
    const [cargandoToken, setCargandoToken] = useState(true);

    const swalModalClasses = useMemo(
        () => ({
            container: darkMode ? "swal2-container--dark--popup" : "swal2-container--light--popup",
            popup: "crm-postventa-swal-popup",
            title: "crm-postventa-swal-title",
            htmlContainer: "crm-postventa-swal-html",
            confirmButton: "crm-postventa-swal-btn crm-postventa-swal-btn-confirm",
            cancelButton: "crm-postventa-swal-btn crm-postventa-swal-btn-cancel",
        }),
        [darkMode]
    );

    const redireccionar = useCallback(() => {
        navegacion("/");
    }, [navegacion]);

    const manejarRespuestaNoAutorizada = useCallback(() => {
        sessionStorage.removeItem("token");
        Swal.fire({
            title: "Sesion expirada o token invalido.",
            text: "Inicie sesion nuevamente.",
            imageUrl: logo,
            imageWidth: 100,
            imageHeight: 30,
            imageAlt: "Logo",
            confirmButtonText: "Ok",
            timer: 5000,
            allowOutsideClick: true,
            customClass: {
                container: darkMode ? "swal2-container--dark" : "swal2-container--light",
                confirmButton: "my-swal-button",
            },
        }).then(redireccionar);
    }, [darkMode, redireccionar]);

    const manejarAccesoDenegado = useCallback(() => {
        Swal.fire({
            title: "Acceso denegado.",
            text: "No tiene permisos para este panel.",
            imageUrl: logo,
            imageWidth: 100,
            imageHeight: 30,
            imageAlt: "Logo",
            confirmButtonText: "Ok",
            timer: 5000,
            allowOutsideClick: true,
            customClass: {
                container: darkMode ? "swal2-container--dark" : "swal2-container--light",
                confirmButton: "my-swal-button",
            },
        }).then(redireccionar);
    }, [darkMode, redireccionar]);

    const cargarVinculos = useCallback(async (tokenParam) => {
        const token = tokenParam || getToken();
        if (!token) return;

        setLoadingTabla(true);
        try {
            const response = await fetch(process.env.REACT_APP_API_CRMCUIT_LISTAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const data = await response.json();
            setDatos(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Error al listar CUIT vinculados para monitoreo:", error);
            setDatos([]);
            Swal.fire("Error", "No se pudo cargar el listado de vinculos.", "error");
        } finally {
            setLoadingTabla(false);
        }
    }, [manejarRespuestaNoAutorizada]);

    const cargarOpcionesCuit = useCallback(async (tokenParam) => {
        const token = tokenParam || getToken();
        if (!token) return [];

        try {
            const response = await fetch(process.env.REACT_APP_API_CRMCUIT_CUITSALTA, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return [];
            }

            if (!response.ok) {
                throw new Error(`Error ${response.status}`);
            }

            const data = await response.json();
            return Array.isArray(data) ? data : [];
        } catch (error) {
            console.error("Error al cargar CUIT de alta:", error);
            Swal.fire("Error", "No se pudieron cargar los CUIT de alta.", "error");
            return [];
        }
    }, [manejarRespuestaNoAutorizada]);

    const abrirModalVinculo = useCallback(async (modo, item = null) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        const cuitsAlta = await cargarOpcionesCuit(token);
        const cuitsSeleccionados = item?.cuits || [];
        const opciones = Array.from(new Set([...(cuitsAlta || []), ...cuitsSeleccionados])).sort((a, b) => a.localeCompare(b));

        if (opciones.length === 0) {
            Swal.fire("Error", "No hay CUIT disponibles en Alta para vincular.", "error");
            return;
        }

        const result = await Swal.fire({
            title: modo === "crear" ? "Nuevo vinculo de CUIT" : `Editar vinculo ${item?.vinculoId}`,
            html: `
                <div class="crm-postventa-form">
                    <label class="crm-postventa-label" for="crm-cuits-vinculados">CUIT vinculados</label>
                    <select id="crm-cuits-vinculados" class="crm-postventa-textarea" multiple size="12">
                        ${opciones.map((cuit) => `<option value="${cuit}" ${cuitsSeleccionados.includes(cuit) ? "selected" : ""}>${cuit}</option>`).join("")}
                    </select>
                    <span class="crm-cuit-help">Se toman solo CUIT existentes en Alta. Seleccione 2 o mas.</span>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: modo === "crear" ? "Crear" : "Guardar",
            cancelButtonText: "Cancelar",
            buttonsStyling: false,
            customClass: swalModalClasses,
            focusConfirm: false,
            preConfirm: () => {
                const select = document.getElementById("crm-cuits-vinculados");
                const cuits = Array.from(select?.selectedOptions || []).map((option) => option.value);
                if (cuits.length < 2) {
                    Swal.showValidationMessage("Debes seleccionar al menos 2 CUIT.");
                    return false;
                }
                return { cuits };
            },
        });

        if (!result.isConfirmed || !result.value) return;

        try {
            const response = await fetch(modo === "crear" ? process.env.REACT_APP_API_CRMCUIT_CREAR : process.env.REACT_APP_API_CRMCUIT_EDITAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    Token: token,
                    VinculoId: item?.vinculoId || 0,
                    Cuits: result.value.cuits,
                }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo guardar el vinculo.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            Swal.fire("Ok", modo === "crear" ? "Vinculo creado." : "Vinculo actualizado.", "success");
            await cargarVinculos(token);
        } catch (error) {
            console.error("Error al guardar vinculo de CUIT:", error);
            Swal.fire("Error", error.message || "No se pudo guardar el vinculo.", "error");
        }
    }, [cargarOpcionesCuit, cargarVinculos, manejarRespuestaNoAutorizada, swalModalClasses]);

    const eliminarVinculo = useCallback(async (item) => {
        const token = getToken();
        if (!token) {
            manejarRespuestaNoAutorizada();
            return;
        }

        const confirmacion = await Swal.fire({
            title: "Eliminar vinculo",
            text: `Se eliminara el vinculo ${item?.vinculoId}.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Eliminar",
            cancelButtonText: "Cancelar",
            buttonsStyling: false,
            customClass: swalModalClasses,
        });

        if (!confirmacion.isConfirmed) return;

        try {
            const response = await fetch(process.env.REACT_APP_API_CRMCUIT_ELIMINAR, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ Token: token, VinculoId: item.vinculoId }),
            });

            if (response.status === 401 || response.status === 403) {
                manejarRespuestaNoAutorizada();
                return;
            }

            if (!response.ok) {
                let mensaje = "No se pudo eliminar el vinculo.";
                try {
                    const data = await response.json();
                    mensaje = data?.mensaje || mensaje;
                } catch { }
                throw new Error(mensaje);
            }

            Swal.fire("Ok", "Vinculo eliminado.", "success");
            await cargarVinculos(token);
        } catch (error) {
            console.error("Error al eliminar vinculo:", error);
            Swal.fire("Error", error.message || "No se pudo eliminar el vinculo.", "error");
        }
    }, [cargarVinculos, manejarRespuestaNoAutorizada, swalModalClasses]);

    useEffect(() => {
        let activo = true;

        const verificarToken = async () => {
            const token = getToken();
            if (!token) {
                manejarRespuestaNoAutorizada();
                return;
            }

            try {
                const response = await fetch(apiUrlToken, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ Token: token }),
                });

                if (response.status === 401 || response.status === 403) {
                    manejarRespuestaNoAutorizada();
                    return;
                }

                if (!response.ok) {
                    throw new Error(`Error token ${response.status}`);
                }

                const raw = await response.text();
                let rol = Number(raw);
                if (!Number.isFinite(rol)) {
                    try {
                        rol = Number(JSON.parse(raw));
                    } catch {
                        rol = NaN;
                    }
                }

                if (!Number.isFinite(rol)) {
                    throw new Error("Respuesta de rol invalida");
                }

                if (rol !== 5) {
                    manejarAccesoDenegado();
                    return;
                }

                if (!activo) return;
                await Promise.all([cargarVinculos(token), cargarOpcionesCuit(token)]);
            } catch (error) {
                console.error("Error validando token:", error);
                if (activo) {
                    Swal.fire("Error", "No se pudo validar la sesion en este momento.", "error");
                }
            } finally {
                if (activo) setCargandoToken(false);
            }
        };

        verificarToken();
        return () => {
            activo = false;
        };
    }, [apiUrlToken, cargarOpcionesCuit, cargarVinculos, manejarAccesoDenegado, manejarRespuestaNoAutorizada]);

    if (cargandoToken) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "50vh" }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="d-flex flex-column min-vh-100" translate="no">
            <div className="pt-5">
                <BienvenidaMonitoreoOperativo titulo="Panel Florencia" />
            </div>

            <div>
                <TituloPagina title="CUIT Vinculados" />
            </div>

            <div className="container py-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className={darkMode ? "text-white m-0" : "text-dark m-0"}>
                        CUIT vinculados para monitoreo ({datos.length})
                    </h5>

                    <div className="crm-cuit-top-actions">
                        <Button
                            className={darkMode ? "d-flex btn-check-modal-dark centrado border-0 fs-14 lato-bold crm-cuit-main-btn" : "d-flex btn-check-modal centrado border-0 fs-14 lato-bold crm-cuit-main-btn"}
                            onClick={() => abrirModalVinculo("crear")}
                        >
                            <FontAwesomeIcon className="me-2" icon={faCirclePlus} />
                            Nuevo vinculo
                        </Button>

                        <Button
                            className={darkMode ? "d-flex btn-check-modal-dark centrado border-0 fs-14 lato-bold crm-cuit-main-btn" : "d-flex btn-check-modal centrado border-0 fs-14 lato-bold crm-cuit-main-btn"}
                            onClick={() => navegacion("/Admin/MonitoreoDiarioCuit")}
                        >
                            <FontAwesomeIcon className="me-2" icon={faLink} />
                            Monitoreo Diario
                        </Button>

                        <Button
                            className={darkMode ? "d-flex btn-check-modal-dark centrado border-0 fs-14 lato-bold crm-cuit-main-btn" : "d-flex btn-check-modal centrado border-0 fs-14 lato-bold crm-cuit-main-btn"}
                            onClick={() => cargarVinculos()}
                            disabled={loadingTabla}
                        >
                            <FontAwesomeIcon className="me-2" icon={faRotate} />
                            Actualizar
                        </Button>
                    </div>
                </div>

                <TablaCuitVinculadosOperativo
                    datos={datos}
                    loading={loadingTabla}
                    darkMode={darkMode}
                    onEditar={(item) => abrirModalVinculo("editar", item)}
                    onEliminar={eliminarVinculo}
                />
            </div>

            <div className="py-4 mt-auto">
                <Footer />
            </div>
        </div>
    );
};

export default CuitVinculadosOperativo;

